import styled from "styled-components"
import { Link, useParams } from "react-router-dom"
import { LEGAL_LIBRARY } from "../constants/legal"
import { getLegalDocumentBySlug } from "../content/legalDocuments"

const LegalDocumentPage = () => {
  const { slug } = useParams()
  const document = getLegalDocumentBySlug(slug)

  if (!document) {
    return (
      <Page>
        <Shell>
          <HeaderCard>
            <TopRow>
              <BackLink to="/">Volver al inicio</BackLink>
            </TopRow>
            <Title>Documento no encontrado</Title>
            <Lead>La ruta legal solicitada no existe o todavía no fue configurada.</Lead>
          </HeaderCard>
        </Shell>
      </Page>
    )
  }

  return (
    <Page>
      <Shell>
        <HeaderCard>
          <TopRow>
            <BackLink to="/">Volver al inicio</BackLink>
          </TopRow>
          <Eyebrow>{document.subtitle}</Eyebrow>
          <Title>{document.title}</Title>
          <Lead>{document.lead}</Lead>
          <MetaList>
            {document.meta.map((item) => (
              <MetaItem key={item}>{item}</MetaItem>
            ))}
          </MetaList>
        </HeaderCard>

        <DocsNav>
          {LEGAL_LIBRARY.map((item) => {
            const isActive = item.href.endsWith(`/${document.slug}`)
            return (
              <DocLink key={item.href} to={item.href} $active={isActive}>
                {item.label}
              </DocLink>
            )
          })}
        </DocsNav>

        <Sections>
          {document.sections.map((section) => (
            <SectionCard key={section.title}>
              <SectionTitle>{section.title}</SectionTitle>
              {section.paragraphs?.map((paragraph) => (
                <SectionParagraph key={paragraph}>{paragraph}</SectionParagraph>
              ))}
              {section.bullets?.length ? (
                <BulletList>
                  {section.bullets.map((item) => (
                    <BulletItem key={item}>{item}</BulletItem>
                  ))}
                </BulletList>
              ) : null}
            </SectionCard>
          ))}
        </Sections>
      </Shell>
    </Page>
  )
}

export default LegalDocumentPage

const Page = styled.div`
  min-height: 100vh;
  padding: 32px 18px 56px;
  background:
    radial-gradient(circle at top right, rgba(255, 1, 2, 0.08), transparent 24%),
    linear-gradient(180deg, #faf7f2 0%, #f4efe7 100%);
`

const Shell = styled.div`
  max-width: 860px;
  margin: 0 auto;
`

const HeaderCard = styled.div`
  padding: 28px;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(17, 24, 39, 0.08);
  box-shadow: 0 16px 40px rgba(17, 24, 39, 0.08);
`

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
`

const BackLink = styled(Link)`
  color: #111111;
  text-decoration: none;
  font-size: 13px;
  font-weight: 600;
`

const Eyebrow = styled.div`
  color: #b91c1c;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`

const Title = styled.h1`
  margin: 10px 0 10px;
  color: #111111;
  font-size: clamp(2rem, 4vw, 3rem);
  line-height: 1.05;
`

const Lead = styled.p`
  margin: 0;
  color: #4b5563;
  font-size: 16px;
  line-height: 1.75;
`

const MetaList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 18px;
`

const MetaItem = styled.div`
  padding: 8px 12px;
  border-radius: 999px;
  background: #fff5f5;
  color: #7f1d1d;
  font-size: 12px;
  font-weight: 600;
`

const DocsNav = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
`

const DocLink = styled(Link)`
  padding: 10px 14px;
  border-radius: 999px;
  text-decoration: none;
  font-size: 13px;
  font-weight: 700;
  color: ${({ $active }) => ($active ? "#ffffff" : "#111111")};
  background: ${({ $active }) => ($active ? "#111111" : "rgba(255, 255, 255, 0.88)")};
  border: 1px solid ${({ $active }) => ($active ? "#111111" : "rgba(17, 24, 39, 0.1)")};
`

const Sections = styled.div`
  display: grid;
  gap: 16px;
  margin-top: 18px;
`

const SectionCard = styled.section`
  padding: 24px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(17, 24, 39, 0.08);
`

const SectionTitle = styled.h2`
  margin: 0 0 12px;
  color: #111111;
  font-size: 20px;
`

const SectionParagraph = styled.p`
  margin: 0 0 12px;
  color: #374151;
  font-size: 15px;
  line-height: 1.8;

  &:last-child {
    margin-bottom: 0;
  }
`

const BulletList = styled.ul`
  margin: 0;
  padding-left: 18px;
  color: #374151;
`

const BulletItem = styled.li`
  margin-bottom: 8px;
  line-height: 1.7;

  &:last-child {
    margin-bottom: 0;
  }
`
